import type {
  PageChromeBreadcrumb,
  PageChromeConfig,
} from "@/contexts/PageChromeContext";

type SegmentDefinition = {
  label: string;
  title?: string;
};

const SEGMENTS: Record<string, SegmentDefinition> = {
  dashboard: { label: "ダッシュボード" },
  analytics: { label: "分析" },
  management: { label: "経営ダッシュボード" },
  customers: { label: "顧客管理" },
  events: { label: "イベント管理" },
  admission: { label: "入会管理" },
  code: { label: "入会コード" },
  requests: { label: "申請一覧" },
  terms: { label: "入会利用規約" },
  "attendance-reports": { label: "欠席・遅刻報告" },
  billing: { label: "決済管理" },
  contract: { label: "契約プラン" },
  history: { label: "請求一覧" },
  overdue: { label: "未収金管理" },
  "payment-setup": { label: "決済サービス設定" },
  "direct-debit": { label: "口座振替" },
  subscriptions: { label: "継続課金" },
  "tuition-plans": { label: "料金設定" },
  "ticket-products": { label: "チケット商品" },
  coupons: { label: "クーポン" },
  "coupon-activity": { label: "配布・利用実績" },
  ticketing: { label: "チケット運用" },
  "ticket-reservations": { label: "チケット受付" },
  chat: { label: "チャット" },
  coaches: { label: "コーチ管理" },
  courses: { label: "コース管理" },
  sessions: { label: "開催枠" },
  lessons: { label: "レッスン" },
  members: { label: "会員一覧" },
  children: { label: "受講者一覧" },
  parents: { label: "会員一覧" },
  "identity-review": { label: "同じ受講者の登録を確認" },
  invite: { label: "会員招待" },
  facilities: { label: "施設管理" },
  "closed-dates": { label: "休館日・休講日" },
  instructors: { label: "講師管理" },
  rooms: { label: "教室管理" },
  "reschedule-policy": { label: "振替ルール" },
  "membership-policy": { label: "会員変更ルール" },
  "member-management-mode": { label: "会員の利用形態" },
  "suspension-rules": { label: "休会料金のルール" },
  "attendance-setting": { label: "出欠設定" },
  "chat-support-policy": { label: "チャット受付設定" },
  faqs: { label: "FAQ管理" },
  "grade-programs": { label: "進級制度" },
  "grade-tests": { label: "進級管理" },
  inquiries: { label: "お問い合わせ" },
  invitations: { label: "招待管理" },
  notifications: { label: "お知らせ管理" },
  inbox: { label: "通知一覧" },
  segments: { label: "配信グループ" },
  operator: { label: "スマコミ運営" },
  "data-reconciliation": { label: "データ整理" },
  "app-status": { label: "アプリ配信管理" },
  "tenant-payments": { label: "テナント決済審査" },
  orders: { label: "注文一覧" },
  organization: { label: "組織管理" },
  branding: { label: "組織ロゴ" },
  products: { label: "商品管理" },
  inventory: { label: "在庫管理" },
  settings: { label: "設定" },
  account: { label: "アカウント設定" },
  staff: { label: "スタッフ管理" },
  "notification-settings": { label: "通知・メール設定" },
  shifts: { label: "シフト管理" },
  surveys: { label: "アンケート管理" },
  "trial-applications": { label: "体験申し込み" },
  "trial-form-templates": { label: "フォームテンプレート設定" },
  "trial-settings": { label: "体験受付設定" },
  "course-enrollment-requests": { label: "コース追加申請" },
  "membership-requests": { label: "休退会申請" },
  create: { label: "新規作成" },
  new: { label: "新規作成" },
  edit: { label: "編集" },
};

const ROOT_DESTINATIONS: Record<string, string> = {
  analytics: "/analytics/management",
  admission: "/admission/requests",
  billing: "/billing/history",
  customers: "/customers/members",
  operator: "/operator/tenant-payments",
  organization: "/facilities",
  settings: "/settings/account",
};

const DETAIL_LABELS: Record<string, string> = {
  members: "会員詳細",
  children: "受講者詳細",
  coaches: "コーチ詳細",
  courses: "コース詳細",
  events: "イベント詳細",
  facilities: "施設詳細",
  faqs: "FAQ詳細",
  instructors: "講師詳細",
  lessons: "レッスン詳細",
  notifications: "お知らせ詳細",
  orders: "注文詳細",
  parents: "会員詳細",
  products: "商品詳細",
  rooms: "教室詳細",
  segments: "配信グループ詳細",
  sessions: "開催枠詳細",
  surveys: "アンケート詳細",
  "trial-form-templates": "フォームテンプレート詳細",
};

const EXACT_TITLES: Record<string, string> = {
  "/dashboard": "ダッシュボード",
  "/analytics/management": "経営ダッシュボード",
  "/analytics/customers": "集客・顧客分析",
  "/admission/requests": "入会申請管理",
  "/admission/code": "入会コード",
  "/admission/terms": "入会利用規約",
  "/attendance-reports": "欠席・遅刻報告",
  "/billing/contract": "契約プラン",
  "/billing/history": "請求一覧",
  "/billing/direct-debit": "口座振替",
  "/operator/direct-debit": "口座振替の運営管理",
  "/billing/overdue": "未収金管理",
  "/billing/payment-setup": "決済サービス設定",
  "/billing/pricing": "料金設定",
  "/billing/discount-rules/new": "月謝割引を追加",
  "/billing/discount-rules": "月謝割引",
  "/billing/discount-applications": "割引の適用内訳",
  "/billing/ticket-products/new": "チケット商品を追加",
  "/billing/ticket-products": "チケット商品",
  "/settings/ticketing": "チケット運用",
  "/billing/subscriptions": "継続課金",
  "/billing/tuition-plans": "料金設定",
  "/chat": "チャット",
  "/coaches": "コーチ管理",
  "/courses": "コース管理",
  "/customers/children": "受講者一覧",
  "/customers/children/identity-review": "同じ受講者の登録を確認",
  "/customers/members": "会員一覧",
  "/customers/members/identity-review": "同じ会員の登録を確認",
  "/course-enrollment-requests": "コース追加申請",
  "/events": "イベント管理",
  "/facilities": "施設管理",
  "/faqs": "FAQ管理",
  "/grade-programs": "進級制度管理",
  "/grade-tests": "進級管理",
  "/inquiries": "お問い合わせ",
  "/invitations": "招待管理",
  "/invitations/analytics": "招待分析ダッシュボード",
  "/notifications": "お知らせ管理",
  "/notifications/inbox": "通知一覧",
  "/notifications/segments": "配信グループ",
  "/operator/data-reconciliation": "データ整理",
  "/operator/app-status": "アプリ配信管理",
  "/operator/tenant-payments": "テナント決済審査",
  "/orders": "注文一覧",
  "/organization/branding": "組織情報",
  "/organization/payout-account": "振込先口座",
  "/organization/reschedule-policy": "組織の振替ルール",
  "/organization/membership-policy": "組織の会員変更ルール",
  "/organization/member-management-mode": "会員の利用形態（組織設定）",
  "/organization/attendance-setting": "組織の出欠設定",
  "/organization/chat-support-policy": "組織のチャット受付設定",
  "/products": "商品管理",
  "/products/inventory": "在庫管理",
  "/reschedule-requests": "振替申請管理",
  "/settings/account": "アカウント設定",
  "/settings/notifications": "自分の通知設定",
  "/staff": "スタッフ管理",
  "/staff/notification-settings": "通知・メール設定",
  "/surveys": "アンケート管理",
  "/trial-applications": "体験申し込み",
  "/trial-form-templates": "フォームテンプレート設定",
  "/trial-settings": "体験受付設定",
  "/membership-requests": "休退会申請",
};

function isKnownSegment(segment: string) {
  return Object.hasOwn(SEGMENTS, segment);
}

function detailLabel(previousSegment: string | undefined) {
  if (!previousSegment) return "詳細";
  return DETAIL_LABELS[previousSegment] ?? "詳細";
}

function segmentHref(segments: string[], index: number) {
  const segment = segments[index];
  if (index === 0 && ROOT_DESTINATIONS[segment]) {
    return ROOT_DESTINATIONS[segment];
  }
  return `/${segments.slice(0, index + 1).join("/")}`;
}

/**
 * ページ固有設定がまだない画面でも、現在地を見失わないための既定値を返す。
 * 詳細画面の固有名は各画面の PageHeader / usePageChromeRegistration が上書きする。
 */
export function resolvePageChrome(pathname: string): PageChromeConfig {
  const normalized = pathname.replace(/\/+$/, "") || "/dashboard";
  const segments = normalized.split("/").filter(Boolean);
  const lastSegment = segments.at(-1);
  const previousSegment = segments.at(-2);

  const title =
    EXACT_TITLES[normalized] ??
    (lastSegment && isKnownSegment(lastSegment)
      ? (SEGMENTS[lastSegment].title ?? SEGMENTS[lastSegment].label)
      : detailLabel(previousSegment));

  if (normalized === "/organization/payout-account") {
    return {
      title,
      breadcrumbs: [
        { label: "ホーム", href: "/dashboard" },
        {
          label: "組織情報",
          href: "/organization/branding?workspaceFacility=all",
        },
      ],
    };
  }

  const breadcrumbs: PageChromeBreadcrumb[] = [];
  if (normalized !== "/dashboard") {
    breadcrumbs.push({ label: "ホーム", href: "/dashboard" });
  }

  segments.slice(0, -1).forEach((segment, index) => {
    const previous = segments[index - 1];
    const label = isKnownSegment(segment)
      ? SEGMENTS[segment].label
      : detailLabel(previous);
    const href = segmentHref(segments, index);

    if (breadcrumbs.some((item) => item.href === href)) return;
    breadcrumbs.push({ label, href });
  });

  return { title, breadcrumbs };
}
